import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRef } from 'react'
import { DateRange } from 'react-day-picker'

import { IForm } from 'core-components/classes/models/IForm'
import { lazyRenderWidget } from 'core-components/helpers/lazyLoad'
import { Accordion } from 'core-components/library/accordion'
import { Button } from 'core-components/library/button'
import { DatePicker } from 'core-components/library/datepicker'
import { Dropdown } from 'core-components/library/dropdown'
import { Input } from 'core-components/library/inputs'
import { onWithChildrenCheck } from 'core-components/library/list/helpers'
import { ICheckedValue } from 'core-components/library/list/types'
import { LoaderOverlay } from 'core-components/library/loader'
import { MultiSelect } from 'core-components/library/multiSelect'
import { recalculateWithChildrenValues } from 'core-components/library/multiSelect/helpers'
import { IDataProviderFilter, IFilter, Logic, Operator } from 'core-components/types'

import { prepareValueForMultiselect } from '../helpers/prepareValueForMultiselect'

import { prepareDateRangeValue } from './helpers/prepareDateRangeValue'
import {
  IAccordionFilterProps,
  IAccordions,
  IDataFilterValues,
  IFilterCheckedValues,
  IFilterPanels,
  IFilterValues,
  TClearButtonPosition,
  TFilterValue,
} from './models/IAccordionFilters'

import styles from './AccordionFilter.module.scss'

const CHANGE_THROTTLE_TIMER = 500

export const AccordionFilter = ({
  source,
  editorSources,
  onChange,
  clearButtonVisible,
  dataFields,
  className,
  expandedGroups,
  collapseGroupOnChange,
  value,
}: IAccordionFilterProps) => {
  const [filters, setFilters] = useState<IFilterPanels>({})
  const [filterValues, setFilterValues] = useState<IFilterValues>({})
  const [openedAccordion, setOpenedAccordion] = useState<IAccordions>(
    expandedGroups?.reduce((acc, el) => ({ ...acc, [el]: true }), {}) || {}
  )
  const [filtersCheckedValues, setFiltersCheckedValues] = useState<IFilterCheckedValues>({})
  const [loading, setLoading] = useState<boolean>(false)

  const ignoreValueOnChangeRef = useRef<IFilterValues | null>({})

  const mainClearButtonVisible =
    clearButtonVisible === TClearButtonPosition.Main ||
    clearButtonVisible === TClearButtonPosition.Full

  const rowClearButtonVisible =
    clearButtonVisible === TClearButtonPosition.FilterRow ||
    clearButtonVisible === TClearButtonPosition.Full

  const fetchFiltersPanels = useCallback(async () => {
    source.fetch().then((res) => {
      if (res) {
        const allFilters = res.items
        const filters = {} as IFilterPanels
        const { tabTitle } = dataFields
        allFilters.forEach((el) => {
          const accordionKey = el[tabTitle]
          if (accordionKey in filters) {
            filters[accordionKey].push(el)
          } else {
            const newFilterWithKey = {
              [accordionKey]: [el],
            }
            Object.assign(filters, newFilterWithKey)
          }
        })
        ignoreValueOnChangeRef.current = collectDefaultValues(allFilters)
        setFilters(filters)
      }
    })
  }, [])

  const onFilterChange = (key: string, v: TFilterValue) => {
    setFilterValues((prev) => {
      return {
        ...prev,
        [key]: v,
      }
    })
  }

  const getClearButtonInControl = useCallback((valueKey: string) => {
    return (
      <div
        data-testid={`clearValue_${valueKey}`}
        onClick={() => {
          clearValueByKey(valueKey)
        }}
        className={styles.iconClear}
      >
        <i className={'kbase kbase-close-circle'} />
      </div>
    )
  }, [])

  const renderFilter = useCallback(
    (el: IForm) => {
      const { source, filterField, filterLabel, type, customFilter, defaultValue } = dataFields
      const valueKey = el[filterField]
      if (el[type] === 'text') {
        return (
          <div key={el.EntryId} data-testid={`filterRow_${valueKey}`}>
            <Input
              label={el[filterLabel]}
              endContent={filterValues[valueKey] ? getClearButtonInControl(valueKey) : undefined}
              margin={'sm'}
              value={(filterValues[valueKey] as string) || ''}
              onChange={(value) => {
                onFilterChange(valueKey, value)
              }}
            />
          </div>
        )
      }

      if (el[type] === 'date') {
        return (
          <div key={el.EntryId} data-testid={`filterRow_${valueKey}`}>
            <DatePicker
              mode={'single'}
              clearButtonVisible={true}
              margin={'sm'}
              label={el[filterLabel]}
              onChange={(value) => {
                const dateString = value?.toISOString() || ''
                onFilterChange(valueKey, dateString)
              }}
              value={filterValues[valueKey] ? new Date(filterValues[valueKey] as string) : undefined}
            />
          </div>
        )
      }

      if (el[type] === 'dateRange') {
        const dateRangeValue = prepareDateRangeValue(filterValues[valueKey])
        return (
          <div key={el.EntryId} data-testid={`filterRow_${valueKey}`}>
            <DatePicker
              mode={'range'}
              clearButtonVisible={true}
              margin={'sm'}
              label={el[filterLabel]}
              onChange={(newValue) => {
                onFilterChange(valueKey, newValue || '')
              }}
              value={dateRangeValue}
            />
          </div>
        )
      }

      if (el[type] === 'multiselect' && editorSources) {
        const componentSource = editorSources[el[source]]
        const { keyField, hierarchyRoot, hierarchyField } = componentSource.source

        const additionalFields = {
          setCheckedValues: (newValue: ICheckedValue[]) => {
            setFiltersCheckedValues((prev) => ({ ...prev, [valueKey]: newValue }))
          },
          checkedValues: filtersCheckedValues[valueKey] || [],
          fields: {
            keyField: keyField,
            hierarchyField: hierarchyField,
            hierarchyRoot: hierarchyRoot,
          },
        }
        const values = prepareValueForMultiselect(filterValues[valueKey])

        return (
          <MultiSelect
            key={el.EntryId}
            margin={'sm'}
            source={componentSource.source}
            caption={componentSource.caption}
            closeOnSelect={false}
            useRowCheckbox={(!!hierarchyRoot && !!hierarchyField) || false}
            onDataLoad={(items) => {
              const defaultValues = defaultValue && el[defaultValue]
              const isDefaultValueCalculated = Object.keys(filtersCheckedValues).includes(valueKey)
              if (defaultValues?.length && items.length && !isDefaultValueCalculated) {
                const defaultItems = items.filter((item) => defaultValues.includes(item[keyField]))
                onWithChildrenCheck(defaultItems, true, items, additionalFields)
              }
            }}
            onCheck={(item, value, items) => {
              if (dataFields.hierarchyField) {
                const newValues = recalculateWithChildrenValues(
                  item,
                  value,
                  items,
                  additionalFields,
                  el[dataFields.hierarchyField]
                )
                onFilterChange(valueKey, newValues)
              }
            }}
            checkedValues={filtersCheckedValues[valueKey]}
            hierarchyField={dataFields.hierarchyField && el[dataFields.hierarchyField]}
            onChange={(newValue, items) => {
              if (!!hierarchyRoot && !!hierarchyField) {
                if (!newValue.length) {
                  setFiltersCheckedValues((prev) => ({ ...prev, [valueKey]: [] }))
                  onFilterChange(valueKey, newValue)
                  return
                }
                const diffValue = ((filterValues[valueKey] as string[]) || []).find(
                  (value) => !newValue.includes(value)
                )
                if (diffValue) {
                  const removedItem = items.find((item) => item[keyField] === diffValue)
                  if (removedItem && dataFields.hierarchyField) {
                    const newValues = recalculateWithChildrenValues(
                      removedItem,
                      false,
                      items,
                      additionalFields,
                      el[dataFields.hierarchyField]
                    )
                    onFilterChange(valueKey, newValues)
                    return
                  }
                }
              }
              onFilterChange(valueKey, newValue)
            }}
            value={values}
            label={el[filterLabel]}
          />
        )
      }

      if (el[type] === 'dropdown' && editorSources) {
        const componentSource = editorSources[el[source]]
        return (
          <Dropdown
            __testId={`dropdown_${valueKey}`}
            __targetTestId={`dropdownTarget_${valueKey}`}
            source={componentSource.source}
            key={el.EntryId}
            margin={'sm'}
            endContent={filterValues[valueKey] ? getClearButtonInControl(valueKey) : undefined}
            label={el[filterLabel]}
            caption={componentSource.caption}
            onChange={(value) => {
              onFilterChange(valueKey, value)
            }}
            value={(filterValues[valueKey] as string) || ''}
          />
        )
      }

      if (el[type] === 'customFilter' && customFilter && el[customFilter]) {
        const customFilterProps: IDataFilterValues = {
          field: valueKey,
          filter: filterValues[valueKey],
          dataFilterValues: Object.entries(dataFields).reduce(
            (acc, [key, value]) => {
              acc[key] = el[value]
              return acc
            },
            {} as IDataFilterValues['dataFilterValues']
          ),
          onFilterChange: (value: IDataProviderFilter | IFilter | null) => {
            if (!value) {
              clearValueByKey(valueKey)
              return
            }
            onFilterChange(valueKey, value)
          },
        }
        return lazyRenderWidget(el[customFilter], customFilterProps)
      }

      return null
    },
    [editorSources, filterValues, filtersCheckedValues]
  )

  const clearValueByKey = (filterValueKey: string) => {
    setFilterValues((prev) => {
      const { [filterValueKey]: deletedKey, ...otherKeys } = prev
      return otherKeys
    })
  }

  const collectDefaultValues = (filtersArray: IForm[]) => {
    const { defaultValue, filterField } = dataFields
    const values = {}
    filtersArray.forEach((filter) => {
      const valueKey = filter[filterField]
      if (defaultValue && filter[defaultValue]) {
        Object.assign(values, { [valueKey]: filter[defaultValue] })
      }
    })
    setFilterValues(values)
    return values
  }

  const clearPanelValues = (panelKey: string) => {
    const { filterField } = dataFields
    const panelFiltersFields = filters[panelKey].map((filter) => filter[filterField])

    setFilterValues((prev) => {
      return Object.keys(prev)
        .filter((key) => !panelFiltersFields.includes(key))
        .reduce((obj, key) => {
          obj[key] = prev[key]
          return obj
        }, {})
    })
  }

  const clearAllPanelValues = () => {
    setFilterValues({})
  }

  const getFilterByFilterField = (field: string): IForm | null => {
    const { filterField } = dataFields
    let foundFilter = null
    Object.keys(filters).forEach((key) => {
      const filter = filters[key].find((filter) => filter[filterField] === field)
      if (filter) {
        foundFilter = { ...filter }
      }
    })
    return foundFilter
  }

  useEffect(() => {
    setLoading(true)
    fetchFiltersPanels().finally(() => {
      setLoading(false)
    })
  }, [fetchFiltersPanels])

  useEffect(() => {
    if (value && Object.keys(value).length) {
      const newValue = { ...value }
      setFilterValues(newValue)
      ignoreValueOnChangeRef.current = newValue
    }
  }, [value, filters])

  useEffect(() => {
    if (JSON.stringify(ignoreValueOnChangeRef.current) === JSON.stringify(filterValues)) {
      return
    }
    ignoreValueOnChangeRef.current = null
    const newFilter: IDataProviderFilter = {
      logic: Logic.AND,
      filters: [],
    }
    Object.keys(filterValues).forEach((field) => {
      const filter = getFilterByFilterField(field)
      if (filter) {
        const { type } = dataFields
        switch (filter[type]) {
          case 'text':
            newFilter.filters.push({
              field,
              operator: Operator.CONTAINS,
              value: filterValues[field] as string,
            })
            break
          case 'date':
            newFilter.filters.push({
              field,
              operator: Operator.EQUAL,
              value: filterValues[field] as string,
            })
            break
          case 'dateRange':
            const drValue = filterValues[field] as DateRange

            if (drValue.from || drValue.to) {
              newFilter.filters.push({
                field,
                operator: Operator.GTE,
                value: drValue.from?.toISOString() as string,
              })
              newFilter.filters.push({
                field,
                operator: Operator.LTE,
                value: drValue.to?.toISOString() as string,
              })
            }

            break
          case 'multiselect':
            const values = prepareValueForMultiselect(filterValues[field])

            newFilter.filters.push({
              logic: Logic.OR,
              filters: values.map((el) => {
                return {
                  operator: Operator.EQUAL,
                  field,
                  value: el,
                }
              }),
            })
            break
          case 'dropdown':
            newFilter.filters.push({
              field,
              operator: Operator.EQUAL,
              value: filterValues[field] as string,
            })
            break
          case 'customFilter':
            newFilter.filters.push(filterValues[field] as IDataProviderFilter)
        }
      }
    })
    const handler = setTimeout(() => {
      onChange(newFilter)
    }, CHANGE_THROTTLE_TIMER)
    return () => {
      clearTimeout(handler)
    }
  }, [filterValues])

  const content = useMemo(() => {
    const groups = Object.keys(filters)
    if (groups.length === 1) {
      const group = groups[0]
      return <section>{filters[group].map((filter) => renderFilter(filter))}</section>
    }
    return groups.map((el, i) => (
      <Accordion
        __testId={`accordionFilterGroup${i}`}
        key={el}
        title={el}
        className={'margin-bottom-sm'}
        buttonClassName={rowClearButtonVisible ? '' : styles.filter_accordion}
        endContent={() => {
          if (rowClearButtonVisible) {
            return (
              <Button
                __testId={`rowButtonClear${i}`}
                variant={'text'}
                onClick={(e) => {
                  e.stopPropagation()
                  clearPanelValues(el)
                }}
              >
                Clear
              </Button>
            )
          }
          return <></>
        }}
        opened={openedAccordion[el]}
        onOpen={(status) =>
          setOpenedAccordion((prev) => {
            if (collapseGroupOnChange) {
              return { [el]: status }
            }
            return { ...prev, [el]: status }
          })
        }
      >
        <section className={styles.filter_root}>
          {filters[el].map((filter) => renderFilter(filter))}
        </section>
      </Accordion>
    ))
  }, [filters, renderFilter, rowClearButtonVisible, openedAccordion])

  return (
    <div className={classNames(className || '', styles.root)}>
      <LoaderOverlay show={loading} />
      {content}
      {mainClearButtonVisible && (
        <Button
          __testId={'clearAllButton'}
          className={styles.bottom_button}
          variant={'outlined'}
          onClick={(e) => {
            e.stopPropagation()
            clearAllPanelValues()
          }}
        >
          Clear all
        </Button>
      )}
    </div>
  )
}
